'use client';

import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import {
    RecaptchaFallbackDecision,
    RecaptchaFormProvider,
    RecaptchaV3Action,
    useFormWithRecaptcha,
} from 'google-recaptcha/components';
import { useRouter } from 'next/navigation';
import { type JSX, PropsWithChildren, useCallback } from 'react';

import { LoginErrorType, useLoginForm } from './LoginFormContext';
import type { CredentialLoginBody, LoginResponse } from './LoginSchemas';

export function LoginForm({
    children,
    redirectAfterLogin,
}: { redirectAfterLogin?: string } & PropsWithChildren): JSX.Element {
    const router = useRouter();
    const { setLoginErrorType, externalAuthId } = useLoginForm();

    const redirectAfterSuccess = useCallback(
        (redirectTo: string | undefined): void => {
            const target = redirectTo ?? '/';
            if (target.startsWith('http')) {
                window.location.href = target;
            } else {
                router.push(target);
            }
        },
        [router],
    );

    const onSubmit = useCallback(
        async (
            formData: FormData,
            recaptcha: { token: string | undefined; version: 'v2' | 'v3' },
        ): Promise<RecaptchaFallbackDecision> => {
            const loginFormPayload: CredentialLoginBody = {
                email: formData.get('email') as string,
                password: formData.get('password') as string,
                agreementCheckbox: (formData.get('agreement-checkbox') as string) === 'on',
                recaptchaToken: recaptcha.token,
                recaptchaVersion: recaptcha.version,
                redirectAfterLogin,
                externalAuthId,
            };
            const response = await axios.post<LoginResponse>('/api/login', loginFormPayload);

            if (!response.data.success && response.data.reason === 'recaptchaChallengeRequired') {
                // Low v3 score (or a failed v2): reveal the checkbox rather than surfacing an error.
                return RecaptchaFallbackDecision.NeedsV2Challenge;
            }
            if (response.data.success) {
                // End the current RUM session so the post-login page starts a fresh one that is
                // re-sampled with the now-authenticated segment. RUM does not rotate the session on
                // login by itself, so a member who signed in mid-session would otherwise keep the
                // anonymous session's sampling decision.
                datadogRum.stopSession();
                redirectAfterSuccess(response.data.redirectTo);
                return RecaptchaFallbackDecision.Handled;
            }
            // reCAPTCHA passed but the credentials were wrong; the v2 token (if any) is spent, so
            // refresh the checkbox for the retry. In v3 mode this is a no-op — no checkbox is shown.
            setLoginErrorType(LoginErrorType.INVALID_CREDENTIALS);
            return RecaptchaFallbackDecision.RefreshV2Challenge;
        },
        [redirectAfterLogin, redirectAfterSuccess, setLoginErrorType, externalAuthId],
    );

    const handleError = useCallback(() => {
        setLoginErrorType(LoginErrorType.INVALID_CREDENTIALS);
    }, [setLoginErrorType]);

    const recaptcha = useFormWithRecaptcha({
        action: RecaptchaV3Action.LOGIN_WITH_CREDENTIALS,
        onSubmit,
        onError: handleError,
    });

    return (
        <RecaptchaFormProvider value={recaptcha}>
            <form aria-label="Login Form" className="w-full" onSubmit={recaptcha.handleSubmit} noValidate>
                {children}
            </form>
        </RecaptchaFormProvider>
    );
}
